import { Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { NotificationData } from '../services/notification.service';
import { ApiService } from '../services/api.service';
import { WayNormalizedPurchase } from '../components/way-purchase-modal/models/way-purchase.models';
import { isRedeemableWayPurchase } from '../components/way-purchase-modal/utils/way-purchase-lookup-orchestration.util';
import {
  GET_LP_IMAGE_PREPAID_REDEEM_FLOW_STATUS,
  isPrepaidRedeemLprNotification,
  normalizePrepaidRedeemNotificationForModal,
} from '../shared/utils/get-lp-image-prepaid-redeem.util';
import { LPR_MODAL_SSE_EVENTS } from '../shared/constants/realtime.constants';
import { adaptPausedSubscriptionLpr } from '../shared/utils/paused-subscription-notification.util';
import { notificationMatchesSelectedLane } from '../shared/utils/gate-lane.util';

export interface LprEntriesParseResult {
  notificationsData: any[];
  paginationData: { total: number; page: number; limit: number; totalPages: number } | null;
}

/**
 * Parse LPR entries API response (multiple possible formats).
 * Use in notifications-list to avoid duplicating format detection.
 */
export function parseLprEntriesApiResponse(
  response: any,
  currentPage: number,
  rowsPerPage: number
): LprEntriesParseResult {
  if (!response?.status || !response?.data) {
    return { notificationsData: [], paginationData: null };
  }
  let notificationsData: any[] = [];
  let paginationData: { total: number; page: number; limit: number; totalPages: number } | null =
    null;

  if (response.data.data && Array.isArray(response.data.data) && response.data.pagination) {
    notificationsData = response.data.data;
    paginationData = response.data.pagination;
  } else if (response.pagination && Array.isArray(response.data)) {
    notificationsData = response.data;
    paginationData = response.pagination;
  } else if (Array.isArray(response.data)) {
    notificationsData = response.data;
    const total = response.total ?? response.data.length;
    paginationData = {
      total,
      page: currentPage + 1,
      limit: rowsPerPage,
      totalPages: Math.ceil(total / rowsPerPage),
    };
  }
  return { notificationsData, paginationData };
}

/**
 * Transform raw LPR entries to notification format and sort by timestamp (newest first).
 */
export function transformAndSortLprEntries(
  entries: any[]
): { event: string; data: NotificationData; timestamp: string }[] {
  const notifications: { event: string; data: NotificationData; timestamp: string }[] = [];
  entries.forEach((entry: any) => {
    const notification = NotificationUtils.transformApiEntryToNotification(entry);
    if (notification) notifications.push(notification);
  });
  notifications.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
  return notifications;
}

/**
 * Merge persisted `event_payload` (Way purchase snapshot) onto an LPR row so modal reopen
 * has nested `purchase` / `purchase_options` after page reload.
 * Only known snapshot fields are applied so row identity/event remain authoritative.
 */
const LPR_EVENT_PAYLOAD_SNAPSHOT_KEYS = [
  'purchase',
  'purchase_options',
  'paused_options',
  'selection_kind',
  'reason',
  'local_package_id',
  'source_events',
  'way_pass_id',
  'purchase_type',
  // CWS-1650: membership status strip on New Order Request.
  'refusal_detail',
  // CWS-1730: an active member's status line on the Active Subscription popup.
  'membership_status',
] as const;

export function mergeLprEventPayloadIntoRow(row: Record<string, any>): Record<string, any> {
  const raw = row['event_payload'];
  let payload: Record<string, any> | null = null;
  if (raw != null && typeof raw === 'object' && !Array.isArray(raw)) {
    payload = raw as Record<string, any>;
  } else if (typeof raw === 'string' && raw.trim()) {
    try {
      const parsed = JSON.parse(raw) as any;
      if (parsed != null && typeof parsed === 'object' && !Array.isArray(parsed)) {
        payload = parsed as Record<string, any>;
      }
    } catch {
      payload = null;
    }
  }
  if (!payload) {
    return row;
  }
  const snapshot: Record<string, any> = {};
  for (const key of LPR_EVENT_PAYLOAD_SNAPSHOT_KEYS) {
    if (Object.prototype.hasOwnProperty.call(payload, key)) {
      snapshot[key] = payload[key];
    }
  }
  if (!Object.keys(snapshot).length) {
    return row;
  }
  return {
    ...row,
    ...snapshot,
  };
}

/**
 * Query params for the header notification bell badge API only.
 * GET order/lpr-entries/location/:id?filter_by=today[&lane_id=...]
 */
export function buildHeaderNotificationBadgeLprQueryParams(
  laneId?: string | null
): Record<string, string> {
  const params: Record<string, string> = {
    filter_by: 'today',
  };
  const normalizedLaneId = laneId != null ? String(laneId).trim() : '';
  if (normalizedLaneId) {
    params['lane_id'] = normalizedLaneId;
  }
  return params;
}

/** Pagination + search options for {@link fetchLprEntries}. */
export interface FetchLprEntriesOptions {
  /** 0-based UI page index (converted to the API's 1-based page). */
  page: number;
  /** Rows per page. */
  limit: number;
  /** Optional license plate search term (server-side, partial match). */
  search?: string;
  /** Optional page lane filter (`lane_id` query param; omit/empty = all lanes). */
  laneId?: string | null;
}

/**
 * 0-based page to refetch when an empty page came back because the total shrank below it
 * (refresh / live removals); null when the current page is still in range.
 */
export function resolveOutOfRangeListPage(
  currentPage: number,
  rowsPerPage: number,
  totalRecords: number,
  rowsReturned: number
): number | null {
  const lastPage = Math.max(0, Math.ceil(totalRecords / rowsPerPage) - 1);
  return rowsReturned === 0 && currentPage > lastPage ? lastPage : null;
}

/**
 * Fetches and parses LPR entries for a location page from the API.
 * Used by notifications-list to avoid duplicating
 * the params building + API call + parseLprEntriesApiResponse + transformAndSortLprEntries pipeline.
 */
export function fetchLprEntries(
  apiService: ApiService,
  locationId: string,
  options: FetchLprEntriesOptions
): Observable<{
  newNotifications: ReturnType<typeof transformAndSortLprEntries>;
  paginationData: LprEntriesParseResult['paginationData'];
}> {
  const { page, limit, search, laneId } = options;
  const params: any = {
    page: (page + 1).toString(),
    limit: limit.toString(),
  };
  const trimmedSearch = (search ?? '').trim();
  if (trimmedSearch) {
    params.search = trimmedSearch;
  }
  const normalizedLaneId = laneId != null ? String(laneId).trim() : '';
  if (normalizedLaneId) {
    params['lane_id'] = normalizedLaneId;
  }
  return apiService.get(`order/lpr-entries/location/${locationId}`, params).pipe(
    map((response: any) => {
      const { notificationsData, paginationData } = parseLprEntriesApiResponse(
        response,
        page,
        limit
      );
      const newNotifications = transformAndSortLprEntries(notificationsData);
      return { newNotifications, paginationData };
    })
  );
}

/**
 * Shared notification utility functions.
 * Extracted from notifications-list and notification-dropdown components
 */
const TERMINAL_LPR_EVENT_TYPES: ReadonlySet<string> = new Set([
  'order-success',
  'order-completed',
  'active-subscription',
  'paused-subscription',
  'exit-active-subscription',
  'order-not-found',
  'vehicle-not-found',
  'fleet-vehicle-found',
  'detailing-appointment-found',
  'exit-detailing-appointment-found',
  'fleet-invoice-added',
  'way-single-wash',
  'way-active-subscription',
  'way-purchase-selection',
  'way-purchase-unredeemable',
  'way-subscription-already-redeemed-today',
  'multi-eligible-selection',
]);

const STALE_REQUEST_LPR_EVENT_TYPES: ReadonlySet<string> = new Set([
  'create-customer-request',
  'create-order-request',
  'invalid-license-plate',
]);

/**
 * True when a payload's `message` is copy written for the attendant rather than
 * an event slug. Rows and some realtime payloads have historically carried the
 * event name in `message` (e.g. "way-active-subscription"), which must never
 * reach a card; anything else is the backend's own sentence and wins over the
 * canned per-event text.
 *
 * @param message The candidate copy
 * @param eventNames Event names this payload is known by; a message equal to one
 *   of them is a slug, not copy
 */
export function isAttendantFacingLprMessage(
  message: string | null | undefined,
  ...eventNames: (string | null | undefined)[]
): boolean {
  const raw = String(message ?? '').trim();
  if (!raw) {
    return false;
  }
  if (eventNames.some(name => !!name && String(name).trim() === raw)) {
    return false;
  }
  return !TERMINAL_LPR_EVENT_TYPES.has(raw) && !STALE_REQUEST_LPR_EVENT_TYPES.has(raw);
}

/**
 * Request types an entry update can be inferred as when it names no `event`: the stale
 * request events, plus `create-payment-request`, which a row with an `order_id` infers to.
 */
const INFERRED_REQUEST_LPR_EVENT_TYPES: ReadonlySet<string> = new Set([
  ...STALE_REQUEST_LPR_EVENT_TYPES,
  'create-payment-request',
]);

/**
 * CWS-1748: the notification an open modal should show after an `lpr-axis-entry-updated`
 * for its own read.
 *
 * Some updates (vehicle enrichment, image fixes) carry no `event`, so their type is only
 * inferred: a row without `event` infers to an order or payment request. Applied
 * as-is, that turned an open Active Subscription modal for a redeemed read into
 * "New Order Request" and dropped its subscription data. An inferred request type
 * therefore never replaces a terminal one: the update's fields are merged into the
 * current notification instead. An update that names its `event` still applies as sent.
 *
 * @param current The notification the modal is showing
 * @param incoming The notification built from the entry update
 */
export function mergeLprEntryUpdateIntoOpenNotification(
  current: NotificationData,
  incoming: NotificationData
): NotificationData {
  const incomingData = (incoming.data ?? {}) as Record<string, any>;
  const explicitEvent = String(incomingData['event'] ?? '').trim();
  const keepsCurrentType =
    !explicitEvent &&
    TERMINAL_LPR_EVENT_TYPES.has(String(current.type)) &&
    INFERRED_REQUEST_LPR_EVENT_TYPES.has(String(incoming.type));

  if (!keepsCurrentType) {
    return {
      ...incoming,
      timestamp: current.timestamp,
      data: { ...incomingData, ...carriedLaneFields(current.data, incomingData) },
    };
  }
  return {
    ...current,
    data: { ...((current.data ?? {}) as Record<string, any>), ...incomingData },
  };
}

/** Lane keys an open modal keeps when an entry update arrives without them. */
const OPEN_MODAL_LANE_KEYS = ['lane_id', 'lane_name', 'camera_roi', 'gate_type'] as const;

/**
 * CWS-1870: lane fields from the current notification that the incoming update lacks.
 * A lane-less update (e.g. vehicle enrichment) must not strip the lane from an open
 * modal — POST /order would otherwise fall back to the tablet's own header lane.
 */
function carriedLaneFields(
  currentData: unknown,
  incomingData: Record<string, any>
): Record<string, unknown> {
  const current = (currentData ?? {}) as Record<string, any>;
  const carried: Record<string, unknown> = {};
  for (const key of OPEN_MODAL_LANE_KEYS) {
    const incomingValue = incomingData[key];
    const missing =
      incomingValue === undefined || incomingValue === null || String(incomingValue).trim() === '';
    if (missing && current[key] !== undefined && current[key] !== null) {
      carried[key] = current[key];
    }
  }
  return carried;
}

/** LPR `event` values the frontend must never write on PUT `order/lpr-entries/:id`. */
export const LPR_PUT_FORBIDDEN_EVENTS: ReadonlySet<string> = new Set(['invalid-license-plate']);

/** Strip forbidden LPR PUT events (returns null when the event must not be sent). */
export function sanitizeLprEntryPutEvent(
  event: NotificationData['type'] | string | null | undefined
): NotificationData['type'] | null {
  const normalized = String(event ?? '').trim() as NotificationData['type'];
  if (!normalized || LPR_PUT_FORBIDDEN_EVENTS.has(normalized)) {
    return null;
  }
  return normalized;
}

/** True when the open modal row originated from an invalid-license-plate LPR capture. */
export function isInvalidLicensePlateLprEntryRow(
  data: Record<string, any> | undefined | null,
  modalType?: NotificationData['type']
): boolean {
  if (modalType === 'invalid-license-plate') {
    return true;
  }
  const rawEvent = String(data?.['event'] ?? '').trim();
  return rawEvent === 'invalid-license-plate';
}

/**
 * Order payment states that mean "settled — nothing left to collect". Mirrors the backend's
 * own definition in `resolveAdminOrderNotificationEvent` (PAYMENT_STATUSES.PAID / COMPLETED),
 * so both ends agree on when a payment request is over.
 */
const SETTLED_ORDER_PAYMENT_STATUSES: ReadonlySet<string> = new Set(['paid', 'completed']);

/**
 * True when the LPR row's payment is settled, whatever the row's own event still says.
 *
 * CWS-1389: `order_status` alone left a hole. A paid single wash is moved to `inprogress`
 * by the lane confirm and only reaches `completed` when the completion job runs a minute
 * later, so in between a paid order read as incomplete and the bell offered to collect
 * payment again. The order's payment status is the fact the question is really about.
 */
function isLprEntryPaymentSettled(entry: Record<string, any>): boolean {
  const paymentStatus = String(
    entry['order_payment_status'] ??
      entry['orderPaymentStatus'] ??
      entry['payment_status'] ??
      entry['paymentStatus'] ??
      ''
  )
    .trim()
    .toLowerCase();
  return SETTLED_ORDER_PAYMENT_STATUSES.has(paymentStatus);
}

/** True when the LPR row represents a finished wash / paid order. */
export function isLprEntryCompleted(entry: Record<string, any>): boolean {
  const flag = entry['is_completed'] ?? entry['isCompleted'];
  if (flag === true || flag === 'true' || flag === 1 || flag === '1') {
    return true;
  }
  if (isLprEntryPaymentSettled(entry)) {
    return true;
  }
  const status = String(entry['order_status'] ?? entry['orderStatus'] ?? entry['status'] ?? '')
    .trim()
    .toLowerCase();
  return ['scheduled', 'completed', 'complete', 'paid', 'succeeded'].includes(status);
}

function readWayPurchaseFromLprRow(row: Record<string, any>): WayNormalizedPurchase | null {
  const direct = row['purchase'];
  if (direct && typeof direct === 'object' && !Array.isArray(direct)) {
    return direct as WayNormalizedPurchase;
  }
  const options = row['purchase_options'];
  if (!Array.isArray(options) || options.length !== 1) {
    return null;
  }
  const [option] = options as { source?: string; purchase?: WayNormalizedPurchase }[];
  if (option?.source !== 'way' || !option.purchase) {
    return null;
  }
  return option.purchase;
}

/** Infer Way modal type from persisted `event_payload` when the row `event` is still a request type. */
export function resolveWayPurchaseNotificationTypeFromLprRow(
  entry: Record<string, any>
): NotificationData['type'] | null {
  const row = mergeLprEventPayloadIntoRow(entry);
  const rawEvent = String(row['event'] ?? '').trim();
  if (rawEvent && WAY_TERMINAL_LPR_EVENT_TYPES.has(rawEvent)) {
    return rawEvent as NotificationData['type'];
  }
  if (row['way_unredeemable_handoff'] === true) {
    return null;
  }

  const purchaseOptions = row['purchase_options'];
  if (Array.isArray(purchaseOptions) && purchaseOptions.length >= 2) {
    const wayOptions = purchaseOptions.filter(
      option =>
        option && typeof option === 'object' && (option as { source?: string }).source === 'way'
    );
    if (wayOptions.length >= 2) {
      return 'way-purchase-selection';
    }
  }

  const purchase = readWayPurchaseFromLprRow(row);
  if (!purchase) {
    return null;
  }

  const isMembership =
    String(purchase.purchase_type ?? '')
      .trim()
      .toLowerCase() === 'membership';
  if (isRedeemableWayPurchase(purchase)) {
    return isMembership ? 'way-active-subscription' : 'way-single-wash';
  }
  if (isMembership && purchase.can_redeem === false && purchase.redeemed_today === true) {
    return 'way-subscription-already-redeemed-today';
  }
  return 'way-purchase-unredeemable';
}

/** Display label for redeemable Way membership lane events (bell list + Way modal). */
export const WAY_ACTIVE_SUBSCRIPTION_DISPLAY_LABEL = 'Way Active Subscription';

/** @deprecated Use {@link WAY_ACTIVE_SUBSCRIPTION_DISPLAY_LABEL}. */
export const ACTIVE_WAY_MEMBERSHIP_DISPLAY_LABEL = WAY_ACTIVE_SUBSCRIPTION_DISPLAY_LABEL;

/** True when an LPR row represents a Way app/web membership purchase at the lane. */
export function isWayMembershipLprRow(entry: Record<string, any>): boolean {
  const rawEvent = String(entry['event'] ?? '').trim();
  if (rawEvent === 'way-active-subscription') {
    return true;
  }
  const sourceEvents = entry['source_events'];
  if (Array.isArray(sourceEvents)) {
    if (sourceEvents.some(event => String(event ?? '').trim() === 'way-active-subscription')) {
      return true;
    }
  }
  const purchase = entry['purchase'];
  if (purchase && typeof purchase === 'object' && !Array.isArray(purchase)) {
    const purchaseType = String((purchase as Record<string, any>)['purchase_type'] ?? '')
      .trim()
      .toLowerCase();
    if (purchaseType === 'membership') {
      return true;
    }
  }
  const purchaseType = String(entry['purchase_type'] ?? entry['purchaseType'] ?? '')
    .trim()
    .toLowerCase();
  if (
    purchaseType === 'membership' &&
    String(entry['way_pass_id'] ?? entry['wayPassId'] ?? '').trim()
  ) {
    return true;
  }
  return false;
}

function isMonthlyMembershipLprEntry(entry: Record<string, any>): boolean {
  const rawEvent = String(entry['event'] ?? '')
    .trim()
    .toLowerCase();
  if (rawEvent === 'active-subscription' || rawEvent === 'exit-active-subscription') {
    return true;
  }
  const wash = String(entry['wash_type'] ?? entry['washType'] ?? '')
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_');
  return (
    wash === 'monthly_membership' ||
    wash === 'monthly' ||
    wash === 'subscription' ||
    wash === 'membership'
  );
}

/**
 * Maps a raw LPR API row to the notification type used in the bell, list, and modal.
 * Completed rows with a stale `event` (e.g. create-customer-request) resolve to order-success.
 */
export function resolveNotificationTypeFromLprEntry(
  entry: Record<string, any>
): NotificationData['type'] {
  const merged = mergeLprEventPayloadIntoRow(entry);
  const rawEvent = String(
    merged['event'] ?? entry['event'] ?? ''
  ).trim() as NotificationData['type'];
  const orderId = String(merged['order_id'] ?? merged['orderId'] ?? '').trim();

  if (isPrepaidRedeemLprNotification(rawEvent, merged)) {
    return 'create-order-request';
  }

  if (rawEvent && TERMINAL_LPR_EVENT_TYPES.has(rawEvent)) {
    return rawEvent;
  }

  if (!isLprEntryCompleted(merged)) {
    const wayType = resolveWayPurchaseNotificationTypeFromLprRow(merged);
    if (wayType) {
      return wayType;
    }
  }

  if (isLprEntryCompleted(merged)) {
    return isMonthlyMembershipLprEntry(merged) ? 'active-subscription' : 'order-success';
  }

  if (orderId) {
    if (rawEvent === 'create-payment-request') {
      return 'create-payment-request';
    }
    if (!rawEvent || STALE_REQUEST_LPR_EVENT_TYPES.has(rawEvent)) {
      return 'create-payment-request';
    }
  }

  return rawEvent || 'invalid-license-plate';
}

/**
 * `event` field for PUT on order-request dismiss. Returns null when the row should not be overwritten.
 */
export function resolveLprEntryDismissEvent(
  data: Record<string, any>,
  modalType: NotificationData['type'] | undefined
): NotificationData['type'] | null {
  if (isLprEntryCompleted(data)) {
    return null;
  }
  const orderId = String(data['order_id'] ?? data['orderId'] ?? '').trim();
  if (orderId) {
    return sanitizeLprEntryPutEvent('create-payment-request');
  }
  if (modalType === 'create-customer-request') {
    return sanitizeLprEntryPutEvent(modalType);
  }
  return sanitizeLprEntryPutEvent('create-order-request');
}

/** PUT `order/lpr-entries/:id` when staff manually edits plate in an LPR notification modal row. */
export function lprEntryManualPlateEditFlagPayload(): Record<string, any> {
  return { plate_manually_edited: true };
}

const WAY_TERMINAL_LPR_EVENT_TYPES: ReadonlySet<string> = new Set([
  'way-single-wash',
  'way-active-subscription',
  'way-purchase-selection',
  'way-purchase-unredeemable',
  'way-subscription-already-redeemed-today',
]);

/**
 * Staff handoff from an unredeemable Way modal must stay on create-order-request even when the
 * LPR DB row still has a terminal Way event (hydration would otherwise blank the order-request UI).
 */
export function shouldPreserveCreateOrderRequestType(
  notification: NotificationData,
  entry?: Record<string, any> | null
): boolean {
  if (notification.type !== 'create-order-request') {
    return false;
  }
  const data = (notification.data ?? {}) as Record<string, any>;
  if (data['way_unredeemable_handoff'] === true) {
    return true;
  }
  const entryEvent = String(entry?.['event'] ?? data['event'] ?? '').trim();
  return WAY_TERMINAL_LPR_EVENT_TYPES.has(entryEvent);
}

/** Apply resolved LPR `event` to NotificationData (type + default message). */
function applyResolvedLprEntryType(
  notification: NotificationData,
  entry?: Record<string, any> | null
): NotificationData {
  if (shouldPreserveCreateOrderRequestType(notification, entry)) {
    const data = {
      ...((notification.data ?? {}) as Record<string, any>),
      event: 'create-order-request',
    };
    return normalizePrepaidRedeemNotificationForModal({
      ...notification,
      type: 'create-order-request',
      message:
        notification.message || NotificationUtils.getMessageForEventType('create-order-request'),
      data,
    });
  }

  const row = {
    event: notification.type,
    ...(entry ?? (notification.data as Record<string, any> | undefined) ?? {}),
  };
  const type = resolveNotificationTypeFromLprEntry(row);
  return normalizePrepaidRedeemNotificationForModal({
    ...notification,
    type,
    message: notification.message || NotificationUtils.getMessageForEventType(type),
  });
}

/**
 * Resolves the LPR `event` onto the notification, then shows a single paused membership in the
 * Active Subscription popup (CWS-1544). Every row-to-modal path (bell, rehydrate, lane events)
 * goes through here, so a stored `paused-subscription` row opens the same popup as the live event.
 */
export function applyResolvedLprEntryTypeToNotification(
  notification: NotificationData,
  entry?: Record<string, any> | null
): NotificationData {
  const resolved = applyResolvedLprEntryType(notification, entry);
  const adapted = adaptPausedSubscriptionLpr(
    resolved.type,
    resolved.type === 'paused-subscription' && resolved.data
      ? mergeLprEventPayloadIntoRow(resolved.data)
      : resolved.data
  );
  return adapted.type === resolved.type
    ? resolved
    : { ...resolved, type: adapted.type, data: adapted.data };
}

export class NotificationUtils {
  /**
   * Returns the human-readable message string for a given notification event type.
   */
  static getMessageForEventType(eventType: NotificationData['type']): string {
    const messages: Record<string, string> = {
      'fleet-vehicle-found': 'Fleet vehicle found!',
      'invalid-license-plate': 'Invalid license plate detected',
      'order-success': 'Order completed successfully!',
      'create-payment-request': 'Payment request created',
      'create-customer-request': 'Customer request created',
      'create-order-request': 'Order request created',
      'order-not-found': 'Order not found',
      'order-completed': 'Order completed',
      'detailing-appointment-found': 'Detailing appointment found!',
      'fleet-invoice-added': 'Fleet invoice added!',
      'vehicle-not-found': 'No vehicle found',
      'active-subscription': 'Subscription found. Please proceed to the wash bay.',
      'paused-subscription': 'Membership is paused.',
      'exit-active-subscription': 'Subscription found. Please proceed to the exit.',
      'exit-detailing-appointment-found':
        'Detailing appointment found. Please proceed to the exit.',
      'way-single-wash': 'Recognized at the lane via license plate. Review and confirm the order.',
      'way-active-subscription':
        'Recognized at the lane via license plate. Review and confirm the order.',
      'way-purchase-selection': 'Select purchase to redeem.',
      'multi-eligible-selection': 'Select package to redeem.',
      'way-purchase-unredeemable': 'Way purchase is not redeemable.',
      'way-subscription-already-redeemed-today':
        'Way subscriptions can be redeemed once in 24 hours.',
    };
    return messages[eventType] ?? 'Notification received';
  }

  /**
   * Transforms a raw API history entry into the internal notification format.
   * Returns null if the entry cannot be parsed.
   */
  static transformApiEntryToNotification(
    entry: any
  ): { event: string; data: NotificationData; timestamp: string } | null {
    try {
      const {
        event: entryEvent,
        message: entryMessage,
        event_timestamp,
        plate,
        vehicle_license_plate,
        license_plate,
      } = entry;
      const row = entry && typeof entry === 'object' ? (entry as Record<string, any>) : {};
      const eventType = resolveNotificationTypeFromLprEntry({
        ...row,
        event: entryEvent ?? row['event'],
      });
      const canonicalMessage = NotificationUtils.getMessageForEventType(eventType);
      const rawMessage = String(entryMessage ?? '').trim();
      const rawEvent = String(entryEvent ?? row['event'] ?? '').trim();
      // DB historically stored the event slug as `message` (e.g. "way-active-subscription").
      // Prefer the same human copy SSE uses so reload matches live notifications.
      const message = isAttendantFacingLprMessage(rawMessage, rawEvent, eventType)
        ? rawMessage
        : canonicalMessage;

      const rowPayload: Record<string, any> = mergeLprEventPayloadIntoRow({
        plate: plate || vehicle_license_plate || license_plate,
        vehicle_license_plate: vehicle_license_plate || plate || license_plate,
        ...entry,
      });
      const isPrepaidRedeem = isPrepaidRedeemLprNotification(rawEvent, rowPayload);

      const notificationData: NotificationData = normalizePrepaidRedeemNotificationForModal({
        type: eventType,
        message,
        timestamp: event_timestamp ? new Date(event_timestamp) : new Date(),
        data: rowPayload,
      });

      const listEvent = isPrepaidRedeem ? GET_LP_IMAGE_PREPAID_REDEEM_FLOW_STATUS : eventType;

      return {
        event: listEvent,
        data: notificationData,
        timestamp: event_timestamp || new Date().toISOString(),
      };
    } catch (error) {
      console.error('Error transforming API entry:', error);
      return null;
    }
  }
}

export interface NotificationListItem {
  event: string;
  data: NotificationData;
  timestamp: string;
}

/** True when payload is an LPR row correction/update (not a new notification). */
export function isUpdatedPusherPayload(data: Record<string, any> | undefined | null): boolean {
  if (!data) {
    return false;
  }
  const v = data['updated_pusher'] ?? data['updatedPusher'];
  if (v === true || v === 'true' || v === 1 || v === '1') {
    return true;
  }
  const nested = data['data'];
  if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
    return isUpdatedPusherPayload(nested as Record<string, any>);
  }
  return false;
}

/** Lane SSE events that must open (or queue) the staff notification modal. */
export function isLprModalOpeningSseEvent(type: string): boolean {
  return (LPR_MODAL_SSE_EVENTS as readonly string[]).includes(type);
}

/**
 * Axis repeat reads reuse an LPR row and set `updated_pusher`, but lane outcomes like
 * create-order-request still require a new popup when no modal is open.
 */
export function shouldSuppressNewModalForUpdatedPusher(
  type: string,
  data: Record<string, any> | undefined | null
): boolean {
  return isUpdatedPusherPayload(data) && !isLprModalOpeningSseEvent(type);
}

/** Pusher correction/update for an existing LPR row (`updated_pusher: true`). */
export function isLprEntryUpdatedPusherNotification(notification: NotificationData): boolean {
  return isUpdatedPusherPayload(notification.data as Record<string, any> | undefined);
}

/** True when a notifications list/dropdown row already has enough LPR entry fields to open the modal without refetch. */
export function hasReusableLprEntryRowFromNotificationsList(
  notification: NotificationData | null | undefined
): boolean {
  const d = notification?.data as Record<string, any> | undefined;
  if (!d) {
    return false;
  }
  if (!resolveLprEntryIdFromNotificationData(d)) {
    return false;
  }
  const plate = String(d['plate'] ?? d['vehicle_license_plate'] ?? '').trim();
  return plate.length > 0 && plate !== 'Unknown';
}

/** Stable id used to match a list row to an LPR entry. */
export function resolveLprEntryIdFromNotificationData(
  data: Record<string, any> | undefined | null
): string {
  if (!data) {
    return '';
  }
  const nested = data['data'] as Record<string, any> | undefined;
  return String(
    data['lpr_entry_id'] ??
      data['lprEntryId'] ??
      data['id'] ??
      data['licence_plate_id'] ??
      data['license_plate_id'] ??
      nested?.['lpr_entry_id'] ??
      nested?.['id'] ??
      ''
  ).trim();
}

function pusherPayloadToListItem(notification: NotificationData): NotificationListItem | null {
  const d = notification.data as Record<string, any> | undefined;
  if (!d) {
    return null;
  }
  const ts = d['event_timestamp'] ?? d['triggered_timestamp'] ?? notification.timestamp;
  return NotificationUtils.transformApiEntryToNotification({
    ...d,
    event: notification.type,
    event_timestamp: ts,
    message: notification.message,
  });
}

/** Entry fields from a list row (API + Pusher store ids/plate on `NotificationData.data`). */
function entryPayloadFromNotification(notification: NotificationData): Record<string, any> {
  const payload = (notification.data ?? {}) as Record<string, any>;
  const nested = payload['data'] as Record<string, any> | undefined;
  if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
    const { type: _t, message: _m, timestamp: _ts, data: _d, ...top } = payload;
    return { ...top, ...nested };
  }
  return { ...payload };
}

/** Merge changed fields from an update event into an existing list row. */
export function patchNotificationListItemFromUpdate(
  existing: NotificationListItem,
  incoming: NotificationListItem
): NotificationListItem {
  const mergedEntry = {
    ...entryPayloadFromNotification(existing.data),
    ...entryPayloadFromNotification(incoming.data),
  };
  const mergedData = applyResolvedLprEntryTypeToNotification(
    {
      ...existing.data,
      type: incoming.data.type ?? existing.data.type,
      message: incoming.data.message ?? existing.data.message,
      timestamp: incoming.data.timestamp ?? existing.data.timestamp,
      data: mergedEntry,
    },
    mergedEntry
  );
  return {
    event: mergedData.type || incoming.event || existing.event,
    timestamp: incoming.timestamp || existing.timestamp,
    data: mergedData,
  };
}

/**
 * Patch one list row in-place for `updated_pusher` events — no API reload.
 * Returns `patched: false` when the row is not in the current list (caller skips full refresh).
 */
export function applyLprEntryUpdateToNotificationList(
  list: NotificationListItem[],
  notification: NotificationData
): { list: NotificationListItem[]; patched: boolean } {
  if (!isLprEntryUpdatedPusherNotification(notification)) {
    return { list, patched: false };
  }
  const incoming = pusherPayloadToListItem(notification);
  if (!incoming) {
    return { list, patched: false };
  }
  const incomingId = resolveLprEntryIdFromListItem(incoming);
  if (!incomingId) {
    return { list, patched: false };
  }
  const index = list.findIndex(item => resolveLprEntryIdFromListItem(item) === incomingId);
  if (index === -1) {
    return { list, patched: false };
  }
  const next = list.slice();
  next[index] = patchNotificationListItemFromUpdate(list[index], incoming);
  return { list: next, patched: true };
}

/** Shared cache for header bell dropdown — populated by dropdown load and live notification updates. */
export const headerDropdownNotificationsCache = {
  items: [] as NotificationListItem[],
  /** LPR entry ids marked read locally (optimistic / confirmed) even when cache items are stale or empty. */
  locallyReadLprEntryIds: new Set<string>(),
  fetchedAt: 0,
  TTL_MS: 120_000,

  isFresh(): boolean {
    return this.fetchedAt > 0 && Date.now() - this.fetchedAt < this.TTL_MS;
  },

  set(items: NotificationListItem[]): void {
    this.items = dedupeNotificationListItems(
      mergeKnownReadStateIntoNotificationList(items, this.items)
    );
    this.fetchedAt = Date.now();
  },

  getTop(limit: number): NotificationListItem[] {
    return this.items.slice(0, limit);
  },

  patchInPlace(notification: NotificationData): void {
    const { list, patched } = applyLprEntryUpdateToNotificationList(this.items, notification);
    if (patched) {
      this.items = list;
      this.fetchedAt = Date.now();
    }
  },

  upsertFromNotification(notification: NotificationData): void {
    this.items = upsertNotificationIntoList(this.items, notification);
    this.fetchedAt = Date.now();
  },

  invalidate(): void {
    this.items = [];
    this.locallyReadLprEntryIds.clear();
    this.fetchedAt = 0;
  },
};

/** Merge or patch one LPR row into a notification list (Pusher or PUT response). */
export function upsertNotificationIntoList(
  list: NotificationListItem[],
  notification: NotificationData
): NotificationListItem[] {
  const incoming = pusherPayloadToListItem(notification);
  if (!incoming) {
    return list;
  }

  if (isLprEntryUpdatedPusherNotification(notification)) {
    const { list: next, patched } = applyLprEntryUpdateToNotificationList(list, notification);
    return patched ? next : dedupeNotificationListItems([incoming, ...list]);
  }

  const incomingId = resolveLprEntryIdFromListItem(incoming);
  if (incomingId) {
    const index = list.findIndex(item => resolveLprEntryIdFromListItem(item) === incomingId);
    if (index >= 0) {
      const next = list.slice();
      next[index] = patchNotificationListItemFromUpdate(list[index], incoming);
      return sortNotificationListItemsByTimestamp(next);
    }
  }

  return dedupeNotificationListItems([incoming, ...list]);
}

/**
 * CWS-1870: `updated_pusher` events reach the bell without the popup's selected-lane filter.
 * An update may always patch a row this tablet already shows, but it only adds a NEW row
 * when that row belongs to the tablet's selected lane — otherwise another lane's read lands
 * in this lane's bell and an order taken from it falls back to this tablet's lane.
 */
export function shouldApplyLprEntryUpdateToList(
  lists: NotificationListItem[][],
  notification: NotificationData,
  selectedLaneId?: string | null,
  selectedCameraRoi?: number | null
): boolean {
  if (!isLprEntryUpdatedPusherNotification(notification)) {
    return true;
  }
  const incoming = pusherPayloadToListItem(notification);
  const incomingId = incoming ? resolveLprEntryIdFromListItem(incoming) : '';
  if (
    incomingId &&
    lists.some(list => list.some(item => resolveLprEntryIdFromListItem(item) === incomingId))
  ) {
    return true;
  }
  return notificationMatchesSelectedLane(
    notification.data as Record<string, any> | undefined,
    selectedLaneId,
    selectedCameraRoi
  );
}

function extractLprEntryRowFromPutResponse(response: any): Record<string, any> | null {
  if (!response || typeof response !== 'object') {
    return null;
  }
  const r = response as { status?: boolean; status_code?: number; data?: any };
  const ok = r.status === true || r.status_code === 200;
  if (!ok) {
    return null;
  }
  const raw = r.data;
  const row =
    raw && typeof raw === 'object' && !Array.isArray(raw)
      ? ((raw as Record<string, any>)['data'] ?? raw)
      : null;
  if (!row || typeof row !== 'object' || Array.isArray(row)) {
    return null;
  }
  return row as Record<string, any>;
}

/** Build NotificationData from PUT order/lpr-entries/:id response body. */
export function notificationDataFromLprEntryPutResponse(response: any): NotificationData | null {
  const row = extractLprEntryRowFromPutResponse(response);
  if (!row) {
    return null;
  }
  const transformed = NotificationUtils.transformApiEntryToNotification(row);
  return transformed?.data ?? null;
}

/** Silent bell-dropdown cache sync from in-app NotificationData (e.g. draft save on modal dismiss). */
export function syncNotificationListCacheFromNotificationData(
  notification: NotificationData
): void {
  headerDropdownNotificationsCache.upsertFromNotification(notification);
}

/** Silent bell-dropdown cache sync after PUT order/lpr-entries/:id (any event, e.g. order-success, create-order-request). */
export function syncHeaderDropdownCacheFromLprEntryPutResponse(
  response: any
): NotificationData | null {
  const notification = notificationDataFromLprEntryPutResponse(response);
  if (!notification) {
    return null;
  }
  syncNotificationListCacheFromNotificationData(notification);
  return notification;
}

export function sortNotificationListItemsByTimestamp(
  items: NotificationListItem[]
): NotificationListItem[] {
  return [...items].sort(
    (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
  );
}

/** Stable id for a dropdown/list row (API and Pusher shapes). */
export function resolveLprEntryIdFromListItem(item: NotificationListItem): string {
  const nested = item.data.data as Record<string, any> | undefined;
  const fromNested = resolveLprEntryIdFromNotificationData(nested);
  if (fromNested) {
    return fromNested;
  }
  return resolveLprEntryIdFromNotificationData(item.data as any as Record<string, any>);
}

/** Window event dispatched when one notification's read state changes (dropdown → header badge). */
export const NOTIFICATION_READ_STATE_CHANGED_EVENT = 'notificationReadStateChanged';

/** True when an LPR row / notification payload is marked read in the backend. */
export function isLprEntryMarkedRead(data: Record<string, any> | null | undefined): boolean {
  if (!data) {
    return false;
  }
  const nested = data['data'];
  const raw =
    data['is_read'] ??
    data['isRead'] ??
    (nested && typeof nested === 'object' && !Array.isArray(nested)
      ? ((nested as Record<string, any>)['is_read'] ?? (nested as Record<string, any>)['isRead'])
      : undefined);
  return raw === true || raw === 'true' || raw === 1 || raw === '1';
}

/** True when a dropdown/list row is still unread (`is_read` false or absent). */
export function isNotificationListItemUnread(item: NotificationListItem): boolean {
  const row = (item.data?.data ?? item.data) as Record<string, any> | undefined;
  return !isLprEntryMarkedRead(row);
}

/** Count unread rows in a notification list. */
export function countUnreadNotificationListItems(items: NotificationListItem[]): number {
  return items.filter(isNotificationListItemUnread).length;
}

/**
 * When applying a fetched list, preserve locally known read rows so an in-flight load
 * started before mark-as-read cannot revert optimistic updates (or confirmed reads
 * not yet reflected in a stale response). Only upgrades unread → read; never downgrades.
 */
export function mergeKnownReadStateIntoNotificationList(
  incoming: NotificationListItem[],
  ...existingSources: NotificationListItem[][]
): NotificationListItem[] {
  const readIds = new Set<string>(headerDropdownNotificationsCache.locallyReadLprEntryIds);
  for (const source of existingSources) {
    for (const item of source ?? []) {
      if (!isNotificationListItemUnread(item)) {
        const id = resolveLprEntryIdFromListItem(item);
        if (id) {
          readIds.add(id);
        }
      }
    }
  }
  if (readIds.size === 0) {
    return incoming;
  }

  let merged = incoming;
  for (const id of readIds) {
    const staleUnread = merged.some(
      item => resolveLprEntryIdFromListItem(item) === id && isNotificationListItemUnread(item)
    );
    if (staleUnread) {
      merged = patchNotificationListItemReadState(merged, id, true);
    }
  }
  return merged;
}

/** Patch `is_read` on one list row by LPR entry id (optimistic UI updates). */
export function patchNotificationListItemReadState(
  list: NotificationListItem[],
  lprEntryId: string,
  isRead: boolean
): NotificationListItem[] {
  const normalizedId = String(lprEntryId ?? '').trim();
  if (!normalizedId) {
    return list;
  }
  const index = list.findIndex(item => resolveLprEntryIdFromListItem(item) === normalizedId);
  if (index === -1) {
    return list;
  }
  const existing = list[index];
  const row = { ...((existing.data.data ?? {}) as Record<string, any>), is_read: isRead };
  const nextList = list.slice();
  nextList[index] = {
    ...existing,
    data: {
      ...existing.data,
      data: row,
    },
  };
  return nextList;
}

/** Dispatch badge delta when one notification's read state changes (dropdown / View All → header). */
export function dispatchNotificationReadStateChanged(unreadDelta: number): void {
  if (typeof window === 'undefined') {
    return;
  }
  window.dispatchEvent(
    new CustomEvent(NOTIFICATION_READ_STATE_CHANGED_EVENT, {
      detail: { unreadDelta },
    })
  );
}

function patchHeaderDropdownCacheReadState(lprEntryId: string, isRead: boolean): void {
  const normalizedId = String(lprEntryId ?? '').trim();
  if (!normalizedId) {
    return;
  }
  if (isRead) {
    headerDropdownNotificationsCache.locallyReadLprEntryIds.add(normalizedId);
  } else {
    headerDropdownNotificationsCache.locallyReadLprEntryIds.delete(normalizedId);
  }
  if (!headerDropdownNotificationsCache.isFresh()) {
    return;
  }
  const hasEntry = headerDropdownNotificationsCache.items.some(
    item => resolveLprEntryIdFromListItem(item) === normalizedId
  );
  if (!hasEntry) {
    return;
  }
  headerDropdownNotificationsCache.set(
    patchNotificationListItemReadState(headerDropdownNotificationsCache.items, normalizedId, isRead)
  );
}

export interface MarkNotificationListItemAsReadOptions {
  /** Called after each local list/cache patch (e.g. markForCheck). */
  onListUpdated?: () => void;
}

/**
 * Mark one notification list item as read via PUT order/lpr-entries/:id.
 * No-op when already read or id missing. Optimistically updates the caller's list,
 * keeps the header dropdown cache in sync when the row is cached, and notifies the
 * header badge via {@link dispatchNotificationReadStateChanged}.
 */
export function markNotificationListItemAsRead(
  apiService: ApiService,
  item: NotificationListItem,
  getList: () => NotificationListItem[],
  setList: (list: NotificationListItem[]) => void,
  options?: MarkNotificationListItemAsReadOptions
): void {
  const lprEntryId = resolveLprEntryIdFromListItem(item);
  if (!lprEntryId || !isNotificationListItemUnread(item)) {
    return;
  }

  const applyPatch = (isRead: boolean) => {
    setList(patchNotificationListItemReadState(getList(), lprEntryId, isRead));
    patchHeaderDropdownCacheReadState(lprEntryId, isRead);
    options?.onListUpdated?.();
  };

  applyPatch(true);
  dispatchNotificationReadStateChanged(-1);

  apiService
    .put(`order/lpr-entries/${lprEntryId}`, { is_read: true })
    .pipe(
      tap(response => {
        syncHeaderDropdownCacheFromLprEntryPutResponse(response);
      }),
      catchError(error => {
        console.error('Error marking notification as read:', error);
        applyPatch(false);
        dispatchNotificationReadStateChanged(1);
        return of(null);
      })
    )
    .subscribe();
}

/** Collapse duplicate LPR rows — keeps the newest occurrence per entry id. */
export function dedupeNotificationListItems(items: NotificationListItem[]): NotificationListItem[] {
  const byKey = new Map<string, NotificationListItem>();
  for (const item of sortNotificationListItemsByTimestamp(items)) {
    const id = resolveLprEntryIdFromListItem(item);
    const key = id || `${item.event}:${item.timestamp}`;
    if (!byKey.has(key)) {
      byKey.set(key, item);
    }
  }
  return sortNotificationListItemsByTimestamp([...byKey.values()]);
}

/** Map raw LPR entries API response rows to dropdown list items. */
export function transformLprEntriesResponseToNotificationList(response: {
  status?: boolean;
  data?: any;
}): NotificationListItem[] {
  if (!response?.status || !Array.isArray(response.data)) {
    return [];
  }
  return (response.data as Record<string, any>[])
    .map(entry => NotificationUtils.transformApiEntryToNotification(entry))
    .filter((item): item is NotificationListItem => item != null);
}
